import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
// router
import { useLocation } from 'react-router-dom'
import useAudit from 'hooks/useAudit'
// hooks
import useChat from 'hooks/useChat'
import useForToday from 'hooks/useForToday'
import { useQRCareCenter } from 'hooks/contentful'
// utils
import sendGaPush from 'utils/sendGaPush'
// icons
import {
	ChatRoundedIcon,
	PhoneRoundedIcon,
	CloseIcon,
	StarIcon,
	VerticalDotsIcon
} from 'components/atoms/icons'
// components
import Loading from 'components/atoms/loading/loading'
// types
import './menu-chat-button.scss'
import { CodesPermissions } from 'types/permissions'
import PermissionContainer from '../permission/permission'
// import useUIActions from 'hooks/useUIActions'
import Modal from 'components/atoms/modal/modal'
import DeviceButtons from 'modules/Auth/components/molecules/device-buttons/device-buttons'
import InstallationInstructions from '../../../modules/Auth/components/molecules/installation-instructions/installation-instructions'

const MenuChatButton = () => {
	// location
	const location = useLocation()

	// state
	const [isOpen, setIsOpen] = useState(false)
	const [extendedMenuChatContent, setExtendedMenuChatContent] = useState([])
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [selectedDevice, setSelectedDevice] = useState(undefined)
	// selectedDevice:
	//   - undefined -> no device chosen yet
	//   - true -> Android chosen
	//   - false -> iOS chosen
	//   - null -> web chosen

	// hooks
	const { initChat } = useChat()
	const { openNPSModal } = useForToday()
	const { showQROverlay } = useQRCareCenter()
	const { events, handledSaveEventRegistry } = useAudit()
	// const { handleHideModal } = useUIActions()

	// Ref for timeout
	const openTimeoutRef = useRef(null)

	// content
	const { menuChatContent, loading } = useQRCareCenter()

	useEffect(() => {
		if (menuChatContent) {
			const newItem = {
				title: 'Ajustes',
				textButtons: ['Crear acceso directo']
			}
			setExtendedMenuChatContent([...menuChatContent, newItem])
		}
	}, [menuChatContent])

	// metrics
	const handleSendGaPush = useCallback(({ action = 'ClicFlotante', label }) => {
		sendGaPush({ category: 'SaludContinuarediseno', action, label })
	}, [])

	// actions
	const handleChatAction = useCallback(
		({ caller }) => {
			handledSaveEventRegistry({ eventName: events[5], caller, trackTime: true, timeSpent: 1 })
			if (initChat) initChat()
		},
		[handledSaveEventRegistry, events, initChat]
	)

	const handledOpen = useCallback(() => {
		openTimeoutRef.current = setTimeout(() => setIsOpen(prev => !prev), 400)
	}, [])

	const handleDeviceClick = useCallback(isAndroid => {
		// Update state to show installation instructions directly
		setSelectedDevice(isAndroid)
	}, [])

	useEffect(() => {
		return () => {
			if (openTimeoutRef.current) {
				clearTimeout(openTimeoutRef.current)
			}
		}
	}, [])

	useEffect(() => {
		setIsOpen(false)
	}, [location])

	const memoizedExtendedMenuChatContent = useMemo(
		() => extendedMenuChatContent,
		[extendedMenuChatContent]
	)

	// Open the modal with device selection
	const showCustomModal = () => {
		setSelectedDevice(undefined)
		setIsModalOpen(true)
	}

	// const closeModal = () => {
	// setIsModalOpen(false)
	// handleHideModal()
	// }

	const menuData = useMemo(
		() => [
			{
				codePermission: CodesPermissions.ATENCION_AL_CLIENTE,
				buttons: [
					{
						codePermission: CodesPermissions.CALIFICA_TU_EXPERIENCIA,
						titleButton: memoizedExtendedMenuChatContent[1]?.textButtons[0],
						action: () => {
							handleSendGaPush({ label: 'Califica tu experiencia' })
							openNPSModal()
						},
						icon: <StarIcon color='#EC2B7A' />
					},
					{
						codePermission: CodesPermissions.CHAT_EN_LINEA,
						titleButton: memoizedExtendedMenuChatContent[1]?.textButtons[1],
						action: () => {
							handleSendGaPush({ label: 'Chat en línea' })
							handleChatAction({
								caller: `Menu chat - ${memoizedExtendedMenuChatContent[1]?.title} - ${memoizedExtendedMenuChatContent[1]?.textButtons[1]}`
							})
						},
						icon: <ChatRoundedIcon color='#EC2B7A' />
					}
				]
			},
			{
				codePermission: CodesPermissions.DEFAULT,
				buttons: [
					{
						codePermission: CodesPermissions.DEFAULT,
						titleButton: memoizedExtendedMenuChatContent[2]?.textButtons[0],
						action: showCustomModal,
						icon: <PhoneRoundedIcon color='#EC2B7A' />
					}
				]
			}
		],
		[
			memoizedExtendedMenuChatContent,
			handleSendGaPush,
			showQROverlay,
			openNPSModal,
			handleChatAction
		]
	)

	if (loading) {
		return (
			<div className='m-menu-chat-button__curtain'>
				<Loading small={true} />
			</div>
		)
	}

	if (!memoizedExtendedMenuChatContent || memoizedExtendedMenuChatContent.length === 0) return null

	return (
		<PermissionContainer permissionCode={CodesPermissions.BOTON_ACCESOS_DIRECTOS}>
			{isOpen && <div className='m-menu-chat-button__curtain' />}
			<div className='m-menu-chat-button'>
				{isModalOpen && (
					<Modal onClose={() => setIsModalOpen(false)}>
						{selectedDevice === undefined ? (
							<div className='text-left'>
								<h1 className='pb1'>Crear acceso directo</h1>
								<div className='m-menu-chat-button__devices'>
									<p className='pb1 m-menu-chat-button__description'>
										Necesitamos saber que dispositivo tienes para empezar con este proceso:
									</p>
									<p className='m-menu-chat-button__question'>¿Qué dispositivo estás usando?</p>
								</div>
								<DeviceButtons onDeviceClick={handleDeviceClick} fromBubble={true} />
							</div>
						) : (
							<InstallationInstructions fromAndroid={selectedDevice} fromMagenta={true} />
						)}
					</Modal>
				)}

				{isOpen ? (
					<div className='m-menu-chat-button__buttons'>
						{menuData.map(({ buttons, codePermission }, elementIndex) => {
							const initDelay = elementIndex + buttons.length
							const { title = '' } = memoizedExtendedMenuChatContent[elementIndex]
							return (
								<PermissionContainer key={title} permissionCode={codePermission}>
									<div className='m-menu-chat-button__buttons__content'>
										{title && (
											<span
												className='m-menu-chat-button__buttons__title'
												style={{ animationDelay: `0.${initDelay}s` }}
											>
												{title}
											</span>
										)}
										{buttons.map(({ action, icon, titleButton = '', codePermission }, index) => (
											<PermissionContainer key={titleButton} permissionCode={codePermission}>
												<button
													className='m-menu-chat-button__buttons__blank'
													style={{ animationDelay: `0.${index + initDelay}s` }}
													onClick={() =>
														action({ caller: `Menu chat - ${title} - ${titleButton}` })
													}
												>
													{titleButton}
													{icon && (
														<span className='m-menu-chat-button__buttons__blank__icon'>{icon}</span>
													)}
												</button>
											</PermissionContainer>
										))}
									</div>
								</PermissionContainer>
							)
						})}

						<button className='m-menu-chat-button__buttons__close' onClick={handledOpen}>
							<CloseIcon color='#EC2B7A' />
						</button>
					</div>
				) : (
					<button
						className='m-menu-chat-button__main'
						onClick={() => {
							handleSendGaPush({ label: 'Ayuda' })
							handledOpen()
						}}
					>
						<VerticalDotsIcon color='#fff' />
					</button>
				)}
			</div>
		</PermissionContainer>
	)
}

export default React.memo(MenuChatButton)
